import {
  Box,
  Button,
  Card,
  Heading,
  HStack,
  Image,
  Link,
  List,
  SimpleGrid,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Check } from 'lucide-react'
import pharmaIllustration from '~/assets/illustrations/pharmaIllustration.jpg'
import { cardHover, PageHeader, SectionHeading } from '~/components/shell'

export const Route = createFileRoute('/_app/pharma/')({ component: PharmaDemo })

function PharmaDemo() {
  const navigate = useNavigate()

  return (
    <Box maxW="1400px" mx="auto">
      <PageHeader title="Pharma" subtitle="Pharmaceutical manufacturing solutions" />

      <VStack align="stretch" gap={10}>
        {/* Customer story */}
        <Card.Root overflow="hidden" boxShadow="2xl">
          <Stack direction={{ base: 'column', md: 'row' }}>
            <Image
              objectFit="cover"
              maxW={{ base: '100%', md: '300px' }}
              src={pharmaIllustration}
              alt="Pharma"
            />
            <Stack flex={1} mb="6">
              <Card.Header>
                <Heading size="lg" color="gray.600">
                  Accelerating Time-to-Market While Ensuring Compliance
                </Heading>
              </Card.Header>
              <Card.Body>
                <VStack align="flex-start" gap={4}>
                  <Text>
                    Biomed faced mounting pressure: costly batch failures were delaying product
                    launches, siloed quality data made FDA audit preparation a nightmare, and legacy
                    systems couldn't provide the real-time visibility needed to catch deviations
                    before they became recalls.
                  </Text>
                  <Text>
                    By implementing the{' '}
                    <Link
                      href="https://www.hivemq.com/products/hivemq-platform/"
                      target="_blank"
                      rel="noreferrer"
                      color="blue.500"
                      _hover={{ textDecoration: 'underline' }}
                    >
                      HiveMQ Platform
                    </Link>
                    , Biomed built a Pharma 4.0 data infrastructure that connects bioreactors, QMS
                    systems, MES, and batch records into a unified real-time view - enabling
                    proactive quality control and streamlined compliance.
                  </Text>
                  <Text>
                    HiveMQ's platform reliability ensures zero data loss for audit trails, while
                    real-time analytics catch quality deviations before they impact entire batches.
                  </Text>
                  <Text fontWeight="medium">Business Impact Delivered:</Text>
                  <List.Root pl={4}>
                    <List.Item>
                      <HStack gap={2}>
                        <Box p={0.5} bg="green.100" borderRadius="full">
                          <Check size={12} color="var(--chakra-colors-green-600)" />
                        </Box>
                        <Text fontSize="sm">
                          <b>35% reduction in batch failures</b> through real-time quality parameter
                          monitoring and early deviation detection
                        </Text>
                      </HStack>
                    </List.Item>
                    <List.Item>
                      <HStack gap={2}>
                        <Box p={0.5} bg="green.100" borderRadius="full">
                          <Check size={12} color="var(--chakra-colors-green-600)" />
                        </Box>
                        <Text fontSize="sm">
                          <b>50% faster audit preparation</b> with unified, real-time access to
                          complete quality data and batch records
                        </Text>
                      </HStack>
                    </List.Item>
                    <List.Item>
                      <HStack gap={2}>
                        <Box p={0.5} bg="green.100" borderRadius="full">
                          <Check size={12} color="var(--chakra-colors-green-600)" />
                        </Box>
                        <Text fontSize="sm">
                          <b>Accelerated time-to-market</b> by reducing quality-related production
                          delays and rework
                        </Text>
                      </HStack>
                    </List.Item>
                    <List.Item>
                      <HStack gap={2}>
                        <Box p={0.5} bg="green.100" borderRadius="full">
                          <Check size={12} color="var(--chakra-colors-green-600)" />
                        </Box>
                        <Text fontSize="sm">
                          <b>Continuous compliance</b> with complete data lineage and audit trails
                          for FDA 21 CFR Part 11 requirements
                        </Text>
                      </HStack>
                    </List.Item>
                  </List.Root>
                </VStack>
              </Card.Body>
            </Stack>
          </Stack>
        </Card.Root>

        {/* Platform journey */}
        <VStack align="stretch" gap={4}>
          <SectionHeading title="The Industrial Data Platform for Agentic AI" />
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={6}>
            <Card.Root boxShadow="lg">
              <Card.Header>
                <Heading size="md" color="brand.solid">
                  Connect
                </Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">
                  Bioreactors, QMS, MES, and batch records streaming quality data in real time
                </Text>
              </Card.Body>
            </Card.Root>
            <Card.Root boxShadow="lg">
              <Card.Header>
                <Heading size="md" color="brand.solid">
                  Contextualize
                </Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">
                  Batch data structured into governed quality models with full data lineage
                </Text>
              </Card.Body>
            </Card.Root>
            <Card.Root boxShadow="lg">
              <Card.Header>
                <Heading size="md" color="brand.solid">
                  Analyze
                </Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">
                  Quality deviations and process parameters monitored in real time
                </Text>
              </Card.Body>
            </Card.Root>
            <Card.Root boxShadow="lg">
              <Card.Header>
                <Heading size="md" color="brand.solid">
                  Act
                </Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">
                  Corrective actions triggered before quality issues impact production or compliance
                </Text>
              </Card.Body>
            </Card.Root>
          </SimpleGrid>
        </VStack>

        {/* Data flow, UNS and AI */}
        <VStack align="stretch" gap={4}>
          <SectionHeading title="Data Flow, UNS and AI" />
          <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} gap={6}>
            <Card.Root
              overflow="hidden"
              transition="all 0.3s"
              _hover={cardHover}
              cursor="pointer"
              onClick={() => navigate({ to: '/pharma/flow' })}
              h="100%"
            >
              <Card.Header>
                <Heading size="md">Data Flow</Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">Explore the pharma data flow architecture.</Text>
              </Card.Body>
              <Card.Footer>
                <Button
                  w="100%"
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate({ to: '/pharma/flow' })
                  }}
                >
                  View Flow
                </Button>
              </Card.Footer>
            </Card.Root>

            <Card.Root
              overflow="hidden"
              transition="all 0.3s"
              _hover={cardHover}
              cursor="pointer"
              onClick={() => navigate({ to: '/pharma/explorer' })}
              h="100%"
            >
              <Card.Header>
                <Heading size="md">UNS Explorer</Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">Explore the Unified Namespace in real-time.</Text>
              </Card.Body>
              <Card.Footer>
                <Button
                  w="100%"
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate({ to: '/pharma/explorer' })
                  }}
                >
                  Explore UNS
                </Button>
              </Card.Footer>
            </Card.Root>

            <Card.Root
              overflow="hidden"
              transition="all 0.3s"
              _hover={cardHover}
              cursor="pointer"
              onClick={() => navigate({ to: '/pharma/bot' })}
              h="100%"
            >
              <Card.Header>
                <Heading size="md">AI Assistant</Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">Chat with AI about pharma operations.</Text>
              </Card.Body>
              <Card.Footer>
                <Button
                  w="100%"
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate({ to: '/pharma/bot' })
                  }}
                >
                  Chat with AI
                </Button>
              </Card.Footer>
            </Card.Root>

            <Card.Root
              overflow="hidden"
              transition="all 0.3s"
              _hover={cardHover}
              cursor="pointer"
              onClick={() => navigate({ to: '/pharma/knowledge-graph' })}
              h="100%"
            >
              <Card.Header>
                <Heading size="md">Knowledge Graph</Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">
                  Visualize the pharma UNS as a real-time network graph.
                </Text>
              </Card.Body>
              <Card.Footer>
                <Button
                  w="100%"
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate({ to: '/pharma/knowledge-graph' })
                  }}
                >
                  View Graph
                </Button>
              </Card.Footer>
            </Card.Root>
          </SimpleGrid>
        </VStack>
      </VStack>
    </Box>
  )
}
